import { asAccountId } from "@repo/app-types/entities/account";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  TeammateType,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CustomerTimeline,
  type CustomerTimelineActivity,
} from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineCall } from "@repo/ui/customerTimeline/customerTimelineCall";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { IconPhone } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} from "../../api/entities/customer.js";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderCallLength } from "../teammatePerformance/durationFormatting.js";

export const CustomerDetailPage: FC = () => {
  const routeParams = useParams();
  const accountId = asAccountId(requireRouteParam(routeParams, "accountId"));
  const playbookId = asPlaybookId(requireRouteParam(routeParams, "playbookId"));
  const [selectedTeammateId, setSelectedTeammateId] =
    useState<TeammateId | null>(null);
  const [selectedPlaybookId, setSelectedPlaybookId] =
    useState<PlaybookId | null>(null);
  const { data: playbooks } = useAllMinimalPlaybooksQuery();
  const detailResult = useCustomerDetailQuery(accountId);
  const timelineResult = useCustomerTimelineInfiniteQuery(accountId);
  const detail = detailResult.currentData;
  const pages = timelineResult.currentData?.pages;

  const availablePlaybookIds = useMemo(
    () =>
      new Set(
        (pages ?? []).flatMap((page) =>
          page.calls.map((call) => call.playbook_id),
        ),
      ),
    [pages],
  );
  const playbookOptions = useMemo<
    readonly SimpleSelectOption<PlaybookId | "all">[]
  >(
    () => [
      { value: "all", label: "All playbooks" },
      ...(playbooks ?? [])
        .filter((playbook) => availablePlaybookIds.has(playbook.id))
        .map((playbook) => ({ value: playbook.id, label: playbook.name }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ],
    [availablePlaybookIds, playbooks],
  );
  const teammateOptions = useMemo<
    readonly SimpleSelectOption<TeammateId | "all">[]
  >(() => {
    const namesById = new Map<TeammateId, string>();
    for (const page of pages ?? []) {
      for (const call of page.calls) {
        for (const teammate of call.teammates) {
          namesById.set(
            teammate.id,
            buildTeammateDisplayNames(teammate).primaryDisplayName,
          );
        }
      }
    }
    return [
      { value: "all", label: "All teammates" },
      ...[...namesById]
        .map(([value, label]) => ({ value, label }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [pages]);

  const highlightedActivityIds = useMemo(() => {
    if (
      !pages ||
      (selectedPlaybookId === null && selectedTeammateId === null)
    ) {
      return undefined;
    }
    return new Set(
      pages
        .flatMap((page) => page.calls)
        .filter(
          (call) =>
            (selectedPlaybookId === null ||
              call.playbook_id === selectedPlaybookId) &&
            (selectedTeammateId === null ||
              call.teammates.some(
                (teammate) => teammate.id === selectedTeammateId,
              )),
        )
        .map((call) => call.id),
    );
  }, [pages, selectedPlaybookId, selectedTeammateId]);

  const modalNavigation = useModalNavigation();

  const activities: readonly CustomerTimelineActivity[] = (pages ?? []).flatMap(
    (page) =>
      page.calls.map((call) => ({
        id: call.id,
        dateTime: call.recorded_at,
        dateLabel: DateTime.fromISO(call.recorded_at).toFormat("MMM d, h:mm a"),
        icon: <IconPhone className="size-4" />,
        content: (
          <div className={PH_MASK_CLASS}>
            <CustomerTimelineCall
              title={call.title || "Call recording"}
              openCall={() =>
                modalNavigation.push({
                  pathname: appPaths.playbookCustomerCallTranscript.build({
                    playbookId,
                    accountId,
                    gradingId: call.grading_id,
                  }),
                  search: new URLSearchParams({
                    recordingId: call.id,
                  }).toString(),
                })
              }
              participants={call.teammates.map((teammate) => ({
                id: teammate.id,
                name: buildTeammateDisplayNames(teammate).primaryDisplayName,
                kind:
                  teammate.type === TeammateType.AGENT ? "voiceAgent" : "human",
              }))}
              duration={
                call.duration_seconds == null
                  ? undefined
                  : renderCallLength(call.duration_seconds)
              }
            />
          </div>
        ),
      })),
  );

  const isError =
    (detailResult.isError && !detail) || (timelineResult.isError && !pages);

  const renderTimeline = () => {
    if (isError) {
      return (
        <p className="text-sm text-muted-foreground">
          Unable to load customer.
        </p>
      );
    }

    if (!pages) {
      return <p className="text-sm text-muted-foreground">Loading timeline…</p>;
    }

    return (
      // Where the list starts beneath the pinned header: the title row, the
      // filter bar, and the body's top padding.
      <div className="[--customer-timeline-sticky-top:6.5rem] sm:[--customer-timeline-sticky-top:7.5rem]">
        <CustomerTimeline
          activities={activities}
          highlightedActivityIds={highlightedActivityIds}
        />
        {timelineResult.hasNextPage && (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            className="mt-5"
            onClick={() => void timelineResult.fetchNextPage()}
            isDisabled={timelineResult.isFetchingNextPage}
            isLoading={timelineResult.isFetchingNextPage}
          >
            Load more activity
          </Button>
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <PageHeader
        title={
          <span className={PH_MASK_CLASS}>{detail?.name ?? "Customer"}</span>
        }
        isLoading={detailResult.isLoading}
        filterBar={
          <FilterBar
            isLoading={
              timelineResult.isFetching && !timelineResult.isFetchingNextPage
            }
            leadingItems={
              <SimpleSelect
                label="Playbook"
                isLabelHidden
                options={playbookOptions}
                value={selectedPlaybookId ?? "all"}
                onChange={(value) =>
                  setSelectedPlaybookId(value === "all" ? null : value)
                }
                size={Size.SMALL}
              />
            }
            items={
              <div className={twMerge("contents", PH_MASK_CLASS)}>
                <SimpleSelect
                  label="Teammate"
                  isLabelHidden
                  options={teammateOptions}
                  value={selectedTeammateId ?? "all"}
                  onChange={(value) =>
                    setSelectedTeammateId(value === "all" ? null : value)
                  }
                  size={Size.SMALL}
                />
              </div>
            }
            onReset={
              selectedTeammateId !== null || selectedPlaybookId !== null
                ? () => {
                    setSelectedTeammateId(null);
                    setSelectedPlaybookId(null);
                  }
                : undefined
            }
          />
        }
      />
      <PageBody>{renderTimeline()}</PageBody>
      <NavigationSlideoverContainer />
    </PageLayout>
  );
};
